// Environment (telematic-account) switching — pure storage + parsing helpers.
//
// Context: an admin who belongs to multiple client environments receives the full
// list as `vtspAccounts` (LDAP DN strings) in the login response. We cache that
// list, plus the currently-bound DN, so the topbar switcher can offer it any time
// without a backend round-trip. The actual switch is orchestrated by
// `composables/useEnvironmentSwitcher` (which calls the backend + reloads).
//
// Auth for the Flexigo admin session is cookie-based, so switching handles no
// token in JS — the backend's `Set-Cookie` rebinds the session and we just reload.
// We only clear the account-scoped localStorage caches so the new environment
// can't read the previous one's stale data.

// The environment list this admin can access (array of raw DN strings, verbatim).
export const ENVIRONMENTS_KEY = 'vtspAccounts';
// The DN of the currently-bound environment (one of ENVIRONMENTS_KEY's entries).
export const CURRENT_ENVIRONMENT_KEY = 'currentTelematicAccount';

// localStorage caches that are scoped to the bound environment and MUST be
// dropped on switch so the new environment starts clean. Kept intentionally out
// of this list: ENVIRONMENTS_KEY / CURRENT_ENVIRONMENT_KEY (user-scoped, survive
// switches) and `countryCodes` (global, deliberately preserved across resets).
export const ACCOUNT_SCOPED_KEYS = [
  'campusLocation',
  'workgroups',
  'selectedWorkgroup',
  'depotList',
  'districtList',
  'regionList',
  'cities',
  'taxOffices',
  'statusTypes',
  'companyTypes',
  'mainCampusSelected',
  'webParams',
];

// Extract a DN's values ("uid=a@b,o=ACME,o=VService" → ["a@b","ACME","VService"])
// — the text after each `key=` up to the next comma.
const VALUE_AFTER_EQUALS = /(?<==)[^,]+/g;

function dnValues(dn) {
  if (!dn || typeof dn !== 'string') return [];
  return dn.match(VALUE_AFTER_EQUALS) || [];
}

// Full label ("a@b - ACME - VService") — used by the login account picker
// (selectAccount.vue), which shows the whole DN.
export function formatVtspAccountLabel(dn) {
  const values = dnValues(dn);
  return values.length ? values.join(' - ') : dn || '';
}

// Concise label for the environment switcher: just the organisation ("ACME").
// Drops the leading uid/email and the trailing provider (e.g. "VService"), keeping
// the middle piece(s). Falls back to the full value if the DN isn't the expected
// uid=…,o=ORG,o=VService shape.
export function formatEnvironmentLabel(dn) {
  const values = dnValues(dn);
  const middle = values.length > 2 ? values.slice(1, -1) : values;
  return middle.join(' - ') || dn || '';
}

// Parse the cached DN list into switcher options: { id, name, value }.
// `value` is the untouched DN (round-trips to the backend as `telematicAccount`).
export function parseEnvironments(list) {
  if (!Array.isArray(list)) return [];
  return list.map((dn) => ({ id: dn, name: formatEnvironmentLabel(dn), value: dn }));
}

export function readEnvironments() {
  try {
    const raw = localStorage.getItem(ENVIRONMENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    localStorage.removeItem(ENVIRONMENTS_KEY);
    return [];
  }
}

export function writeEnvironments(list) {
  if (!Array.isArray(list) || !list.length) return;
  localStorage.setItem(ENVIRONMENTS_KEY, JSON.stringify(list));
}

export function readCurrentEnvironment() {
  return localStorage.getItem(CURRENT_ENVIRONMENT_KEY) || null;
}

export function writeCurrentEnvironment(dn) {
  if (dn) localStorage.setItem(CURRENT_ENVIRONMENT_KEY, dn);
}

// Drop the cached environment list + current env. Must run at the start of a
// fresh login: the Flexigo login flow has NO login-time localStorage RESET (only
// common/screens/Login.vue does that, for a different module), so without this a
// previous admin's environments would survive logout and leak into the next
// user's session on a shared browser.
export function clearEnvironments() {
  localStorage.removeItem(ENVIRONMENTS_KEY);
  localStorage.removeItem(CURRENT_ENVIRONMENT_KEY);
}

export function clearTenantCaches() {
  ACCOUNT_SCOPED_KEYS.forEach((key) => localStorage.removeItem(key));
}
